import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import Portal from "elements/Portal";
import type {
  IEmailBuilderMergeTagAutocompletePosition,
  IEmailBuilderMergeTagSuggestion,
  TEmailBuilderMergeTag,
} from "./emailBuilderMergeTagMatching";
import "./emailBuilderMergeTagMatching.scss";

interface IEmailBuilderMergeTagAutocompleteProps {
  suggestions: readonly IEmailBuilderMergeTagSuggestion[];
  activeIndex: number;
  position: IEmailBuilderMergeTagAutocompletePosition;
  listboxId: string;
  onActiveIndexChange: (index: number) => void;
  onSelect: (tag: TEmailBuilderMergeTag) => void;
}

export function EmailBuilderMergeTagAutocomplete(props: IEmailBuilderMergeTagAutocompleteProps) {
  const { t } = useTranslation();
  const { suggestions, activeIndex, position, listboxId, onActiveIndexChange, onSelect } = props;

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <Portal isMounted={true} parentId="portal-root">
      <div
        className="email-builder-merge-tag-autocomplete"
        style={{
          left: position.left,
          top: position.top,
          width: position.maxWidth,
          maxWidth: position.maxWidth,
          maxHeight: position.maxHeight,
        }}
        onMouseDown={event => event.preventDefault()}
        onClick={event => event.stopPropagation()}
      >
        <ul
          id={listboxId}
          className="email-builder-merge-tag-autocomplete-list"
          role="listbox"
          aria-label={t("Merge tags")}
        >
          {suggestions.map((suggestion, index) => {
            const optionId = `${listboxId}-option-${index}`;
            const isActive = index === activeIndex;
            const showTypedPredicted = index === 0 && suggestion.typed.length > 0;

            return (
              <li
                key={suggestion.tag}
                id={optionId}
                className={classNames("email-builder-merge-tag-autocomplete-option", {
                  "email-builder-merge-tag-autocomplete-option--active": isActive,
                })}
                role="option"
                aria-selected={isActive}
                onMouseEnter={() => onActiveIndexChange(index)}
                onMouseDown={event => {
                  event.preventDefault();
                  onSelect(suggestion.tag);
                }}
              >
                <span className="email-builder-merge-tag-autocomplete-label">{t(suggestion.label)}</span>
                <span className="email-builder-merge-tag-autocomplete-insertion">
                  {showTypedPredicted ? (
                    <>
                      <span className="email-builder-merge-tag-autocomplete-typed">
                        {suggestion.typed}
                      </span>
                      <span className="email-builder-merge-tag-autocomplete-predicted">
                        {suggestion.predicted}
                      </span>
                    </>
                  ) : (
                    suggestion.insertion
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </Portal>
  );
}
